/**
 * Sustainability Scoring Agent
 * Evaluates and scores farms based on sustainability practices
 */

interface SustainabilityScore {
  overall: number;
  categories: {
    organic: number;
    waterConservation: number;
    soilHealth: number;
    biodiversity: number;
    energy: number;
    waste: number;
    social: number;
  };
  certification: string[];
  practices: string[];
  improvements: string[];
  badge: 'platinum' | 'gold' | 'silver' | 'bronze' | 'none';
}

interface SustainabilityMetrics {
  waterUsage?: number; // gallons per acre
  energyUsage?: number; // kWh per acre
  wasteReduction?: number; // percentage
  organicCertified: boolean;
  pesticidesUsed: boolean;
  coverCropping: boolean;
  cropRotation: boolean;
  composting: boolean;
  renewableEnergy: boolean;
  rainwaterHarvesting: boolean;
  nativePollinators: boolean;
  fairWages: boolean;
  communityPrograms: boolean;
}

class SustainabilityScoringAgent {
  /**
   * Calculate comprehensive sustainability score
   * @param farmId Farm identifier
   * @param metrics Sustainability metrics
   * @returns Detailed sustainability score
   */
  calculateScore(farmId: string, metrics: SustainabilityMetrics): SustainabilityScore {
    const categories = {
      organic: this.scoreOrganic(metrics),
      waterConservation: this.scoreWaterConservation(metrics),
      soilHealth: this.scoreSoilHealth(metrics),
      biodiversity: this.scoreBiodiversity(metrics),
      energy: this.scoreEnergy(metrics),
      waste: this.scoreWaste(metrics),
      social: this.scoreSocial(metrics),
    };

    // Calculate weighted overall score
    const weights = {
      organic: 0.20,
      waterConservation: 0.15,
      soilHealth: 0.15,
      biodiversity: 0.15,
      energy: 0.15,
      waste: 0.10,
      social: 0.10,
    };

    const overall = Object.entries(categories).reduce((sum, [key, score]) => {
      return sum + score * weights[key as keyof typeof weights];
    }, 0);

    const certification = this.identifyCertifications(metrics);
    const practices = this.identifyPractices(metrics);
    const improvements = this.suggestImprovements(categories);
    const badge = this.determineBadge(overall);

    return {
      overall: Math.round(overall),
      categories,
      certification,
      practices,
      improvements,
      badge,
    };
  }

  /**
   * Compare sustainability scores across farms
   * @param scores Array of farm scores
   * @returns Ranking and insights
   */
  compareFarms(scores: Array<{ farmId: string; score: SustainabilityScore }>): Array<{
    farmId: string;
    rank: number;
    score: number;
    badge: string;
    strengths: string[];
  }> {
    const sorted = scores
      .sort((a, b) => b.score.overall - a.score.overall)
      .map((item, index) => ({
        farmId: item.farmId,
        rank: index + 1,
        score: item.score.overall,
        badge: item.score.badge,
        strengths: this.identifyStrengths(item.score),
      }));

    return sorted;
  }

  /**
   * Get sustainability trends over time
   * @param historicalScores Historical scoring data
   * @returns Trend analysis
   */
  analyzeTrends(historicalScores: Array<{
    date: Date;
    score: number;
  }>): {
    trend: 'improving' | 'stable' | 'declining';
    changeRate: number;
    insights: string[];
  } {
    if (historicalScores.length < 2) {
      return {
        trend: 'stable',
        changeRate: 0,
        insights: ['Insufficient data for trend analysis'],
      };
    }

    const sorted = historicalScores.sort((a, b) => a.date.getTime() - b.date.getTime());
    const first = sorted[0].score;
    const last = sorted[sorted.length - 1].score;
    const changeRate = ((last - first) / first) * 100;

    const trend =
      changeRate > 5 ? 'improving' :
      changeRate < -5 ? 'declining' : 'stable';

    const insights: string[] = [];
    if (trend === 'improving') {
      insights.push('Sustainability practices are improving over time');
      insights.push(`${changeRate.toFixed(1)}% improvement in overall score`);
    } else if (trend === 'declining') {
      insights.push('Sustainability scores have decreased');
      insights.push('Consider reviewing current practices');
    } else {
      insights.push('Maintaining consistent sustainability practices');
    }

    return { trend, changeRate, insights };
  }

  // Scoring Methods

  private scoreOrganic(metrics: SustainabilityMetrics): number {
    let score = 0;
    if (metrics.organicCertified) score += 60;
    if (!metrics.pesticidesUsed) score += 40;
    return Math.min(100, score);
  }

  private scoreWaterConservation(metrics: SustainabilityMetrics): number {
    let score = 50; // Base score
    if (metrics.rainwaterHarvesting) score += 30;
    if (metrics.waterUsage && metrics.waterUsage < 30000) score += 20; // Low water usage
    return Math.min(100, score);
  }

  private scoreSoilHealth(metrics: SustainabilityMetrics): number {
    let score = 0;
    if (metrics.coverCropping) score += 35;
    if (metrics.cropRotation) score += 35;
    if (metrics.composting) score += 30;
    return Math.min(100, score);
  }

  private scoreBiodiversity(metrics: SustainabilityMetrics): number {
    let score = 40; // Base score for traditional farming
    if (metrics.nativePollinators) score += 40;
    if (!metrics.pesticidesUsed) score += 20;
    return Math.min(100, score);
  }

  private scoreEnergy(metrics: SustainabilityMetrics): number {
    let score = 50; // Base score
    if (metrics.renewableEnergy) score += 50;
    if (metrics.energyUsage && metrics.energyUsage < 500) score += 20;
    return Math.min(100, score);
  }

  private scoreWaste(metrics: SustainabilityMetrics): number {
    let score = 50; // Base score
    if (metrics.composting) score += 25;
    if (metrics.wasteReduction && metrics.wasteReduction > 50) score += 25;
    return Math.min(100, score);
  }

  private scoreSocial(metrics: SustainabilityMetrics): number {
    let score = 50; // Base score
    if (metrics.fairWages) score += 25;
    if (metrics.communityPrograms) score += 25;
    return Math.min(100, score);
  }

  // Helper Methods

  private identifyCertifications(metrics: SustainabilityMetrics): string[] {
    const certs: string[] = [];
    if (metrics.organicCertified) certs.push('USDA Organic');
    if (metrics.fairWages) certs.push('Fair Trade');
    if (metrics.rainwaterHarvesting && metrics.waterUsage && metrics.waterUsage < 25000) {
      certs.push('Water Conservation Certified');
    }
    return certs;
  }

  private identifyPractices(metrics: SustainabilityMetrics): string[] {
    const practices: string[] = [];
    if (metrics.coverCropping) practices.push('Cover Cropping');
    if (metrics.cropRotation) practices.push('Crop Rotation');
    if (metrics.composting) practices.push('Composting');
    if (metrics.rainwaterHarvesting) practices.push('Rainwater Harvesting');
    if (metrics.renewableEnergy) practices.push('Renewable Energy');
    if (metrics.nativePollinators) practices.push('Pollinator Habitat');
    if (!metrics.pesticidesUsed) practices.push('Pesticide-Free');
    return practices;
  }

  private suggestImprovements(categories: SustainabilityScore['categories']): string[] {
    const improvements: string[] = [];

    if (categories.organic < 60) {
      improvements.push('Consider organic certification and reducing chemical inputs');
    }
    if (categories.waterConservation < 60) {
      improvements.push('Implement rainwater harvesting and drip irrigation');
    }
    if (categories.soilHealth < 60) {
      improvements.push('Adopt cover cropping and crop rotation practices');
    }
    if (categories.energy < 60) {
      improvements.push('Explore solar or wind energy options');
    }
    if (categories.biodiversity < 60) {
      improvements.push('Create pollinator habitats and preserve native plants');
    }

    return improvements.slice(0, 3); // Top 3 recommendations
  }

  private determineBadge(score: number): SustainabilityScore['badge'] {
    if (score >= 90) return 'platinum';
    if (score >= 75) return 'gold';
    if (score >= 60) return 'silver';
    if (score >= 45) return 'bronze';
    return 'none';
  }

  private identifyStrengths(score: SustainabilityScore): string[] {
    const strengths: string[] = [];
    const categories = Object.entries(score.categories).sort((a, b) => b[1] - a[1]);

    for (const [category, value] of categories.slice(0, 3)) {
      if (value >= 70) {
        strengths.push(this.formatCategoryName(category));
      }
    }

    return strengths;
  }

  private formatCategoryName(category: string): string {
    return category
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }
}

export const sustainabilityScoringAgent = new SustainabilityScoringAgent();
export default sustainabilityScoringAgent;
